import { Box, Stack, Tooltip, Typography } from '@mui/material'

import ChipStatus from 'packages/ui/ChipStatus'
import { type IColumn } from 'packages/ui/Table'

import { formatDate, getStringValue } from '../../utils'

type EmailStatusDotProps = {
  status: unknown
  errorReason: unknown
}

const ACKNOWLEDGED_STATUS = 'acknowledged'

const EmailStatusDot = ({ status: rawStatus, errorReason }: EmailStatusDotProps) => {
  const status = getStringValue(rawStatus)
  const isAcknowledged = status === ACKNOWLEDGED_STATUS
  const tooltipTitle = isAcknowledged ? '' : getStringValue(errorReason)

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <Box
        component="span"
        role="img"
        aria-label={tooltipTitle || status || undefined}
        sx={{
          flexShrink: 0,
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: isAcknowledged ? '#92BC62' : '#DC2626'
        }}
      />
    </Tooltip>
  )
}

const COLUMNS_CONFIG: IColumn[] = [
  {
    name: 'user',
    header: {
      label: (ctx) => ctx.t('mailing.sends.column_user')
    },
    style: {
      minWidth: 160
    },
    value: (item) => {
      const fullName = [
        getStringValue(item.user_firstname),
        getStringValue(item.user_lastname)
      ].filter(Boolean).join(' ')

      return (
        <Typography component="span" sx={{ color: '#464646', fontSize: 12, fontWeight: 500 }}>
          {fullName || '-'}
        </Typography>
      )
    }
  },
  {
    name: 'user_email',
    header: {
      label: () => 'Email'
    },
    style: {
      minWidth: 220
    },
    value: (item) => (
      <Stack component="span" direction="row" alignItems="center" gap={1}>
        <EmailStatusDot status={item.status} errorReason={item.error_reason} />
        <span>{getStringValue(item.user_email) || '-'}</span>
      </Stack>
    )
  },
  {
    name: 'delivery_type',
    header: {
      label: () => 'Type'
    },
    style: {
      minWidth: 120
    },
    value: (item, ctx) => {
      const sourceType = item.delivery_type === 'campaign'
        ? ctx?.t('mailing.sends.column_campaign')
        : item.delivery_type === 'trigger'
          ? ctx?.t('mailing.sends.column_trigger')
          : '-'
      const isCampaign = item.delivery_type === 'campaign'

      return (
        <ChipStatus
          status={sourceType || '-'}
          color={isCampaign ? '#2570EB' : '#9A6700'}
          bgcolor={isCampaign ? '#E4EEFA' : '#FEF4E9'}
          borderRadius="8px"
        />
      )
    }
  },
  {
    name: 'delivery_name',
    header: {
      label: (ctx) => ctx.t('mailing.sends.column_delivery_name')
    },
    style: {
      minWidth: 180
    },
    value: (item) => getStringValue(item.delivery_name) || '-'
  },
  {
    name: 'sent_at',
    header: {
      label: (ctx) => ctx.t('mailing.sends.column_sent_at')
    },
    style: {
      minWidth: 150
    },
    value: (item) => formatDate(item.sent_at)
  }
]

export default COLUMNS_CONFIG
